import { ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import DefaultImage from '@/assets/images/map-style/default.webp'
import DarkImage from '@/assets/images/map-style/dark.webp'
import TerrainImage from '@/assets/images/map-style/terrain.webp'
import SatelliteImage from '@/assets/images/map-style/satellite.webp'
import ArmyImage from '@/assets/images/map-style/army.webp'
import { useI18n } from 'vue-i18n'
import {
  DATA,
  DEM_EXTRACTION,
  MAP_VIEW,
  TASKS,
  CITY_GENERATION,
  DATA_EXPLORATION,
  CHANGE_DETECTION,
  type MenuIconKey,
} from '@/constant/menu'
import { MAP_3D } from '@/constant/map.ts'
export const useSettingStore = defineStore('setting', () => {
  const { t } = useI18n()

  const currentCoordinateSystemType = ref('DD')

  // Utility function to convert decimal degrees to DMS
  const decimalToDMS = (decimal: number, isLatitude: boolean): string => {
    const direction = isLatitude ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W'
    const absDecimal = Math.abs(decimal)
    const degrees = Math.floor(absDecimal)
    const minutes = Math.floor((absDecimal - degrees) * 60)
    const seconds = ((absDecimal - degrees - minutes / 60) * 3600).toFixed(2)
    return `${degrees}° ${minutes}' ${seconds}" ${direction}`
  }

  // Utility function to convert decimal degrees to MGRS
  const decimalToMGRS = (lat: number, lng: number): string => {
    // This is a simplified version. In production, you should use a proper MGRS library
    const zone = Math.floor((lng + 180) / 6) + 1
    const latBand = 'CDEFGHJKLMNPQRSTUVWXX'[Math.floor((lat + 80) / 8)]
    return `${zone}${latBand} ${Math.abs(lng).toFixed(0)} ${Math.abs(lat).toFixed(0)}`
  }
  const listCoordinateSystemType = ref([
    {
      name: t('coordinateSystem.decimalDegrees'),
      title: 'DD',
      value: (lat: number, lng: number) => `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
    },
    {
      name: t('coordinateSystem.degreesMinutesSeconds'),
      title: 'DMS',
      value: (lat: number, lng: number) =>
        `${decimalToDMS(lat, false)}, ${decimalToDMS(lng, true)}`,
    },
    {
      name: t('coordinateSystem.mgrs'),
      title: 'MGRS',
      value: (lat: number, lng: number) => decimalToMGRS(lat, lng),
    },
  ])

  const currentMapStylePath = ref<string>('@/assets/json/default-map-style.json')
  const listMapStyles = ref([
    {
      name: t('mapStyle.default'),
      img: DefaultImage,
      code: 'default',
      path: new URL('@/assets/json/default-map-style.json', import.meta.url).href,
    },
    {
      name: t('mapStyle.dark'),
      img: DarkImage,
      code: 'dark',
      path: new URL('@/assets/json/dark-map-style.json', import.meta.url).href,
    },
    {
      name: t('mapStyle.terrain'),
      img: TerrainImage,
      code: 'terrain',
      path: new URL('@/assets/json/terrain-map-style.json', import.meta.url).href,
    },
    {
      name: t('mapStyle.satellite'),
      img: SatelliteImage,
      code: 'satellite',
      path: new URL('@/assets/json/satellite-map-style.json', import.meta.url).href,
    },
    {
      name: t('mapStyle.army'),
      img: ArmyImage,
      code: 'army',
      path: new URL('@/assets/json/army-map-style.json', import.meta.url).href,
    },
  ])

  const mapMode = ref(MAP_3D)

  const menus = shallowRef([
    {
      index: 1,
      title: 'Terrain Analysis & Visualization',
      value: MAP_VIEW,
      iconKey: 'terrain-analysis' as MenuIconKey,
    },
    {
      index: 2,
      title: '3D City Generation',
      value: CITY_GENERATION,
      iconKey: 'city' as MenuIconKey,
    },
    { index: 3, title: 'DEM Generation', value: DEM_EXTRACTION, iconKey: 'dem' as MenuIconKey },
    { index: 4, title: '3D Data Management', value: DATA, iconKey: 'data' as MenuIconKey },
    { index: 5, title: 'Tasks Management', value: TASKS, iconKey: 'tasks' as MenuIconKey },
    {
      index: 6,
      title: 'Data Exploration',
      value: DATA_EXPLORATION,
      iconKey: 'exploration' as MenuIconKey,
    },
    {
      index: 7,
      title: 'Building Change Detection',
      value: CHANGE_DETECTION,
      iconKey: 'change-detection' as MenuIconKey,
    },
    // { index: 1, title: '3D Upload Data', value: UPLOAD_DATA, icon: IconUpload },
    // {index:6, title: 'Intelligence Orchestration', value: CHAT_ORCHESTRATION, icon: IconIO },
  ])

  // Initial menu: ?menu=<index> URL param overrides, then the persisted
  // choice from the last visit, then the default.
  const resolveInitialMenu = () => {
    const menuIndex = new URLSearchParams(window.location.search).get('menu')
    if (menuIndex) {
      const byIndex = menus.value.find((menu) => menu.index === parseInt(menuIndex))?.value
      if (byIndex) return byIndex
    }
    const stored = localStorage.getItem('currentMenu')
    if (stored && menus.value.some((menu) => menu.value === stored)) {
      return stored
    }
    return DATA
  }
  const currentMenu = ref(resolveInitialMenu())

  watch(currentMenu, (menu) => localStorage.setItem('currentMenu', menu))

  return {
    currentMenu,
    mapMode,
    currentCoordinateSystemType,
    listCoordinateSystemType,
    currentMapStylePath,
    listMapStyles,
    menus,
  }
})
